import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import UserAvatar from "../components/UserAvatar";
import {
  ArrowLeft, Save, Loader2, Mail, KeyRound, User as UserIcon, Globe,
  Eye, EyeOff, Check, X, Sparkles,
} from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { LANGUAGE_OPTIONS, toIsoCode } from "../lib/languageOptions";

export default function AccountPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    navigate("/login");
    return null;
  }
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-4 flex items-center justify-between">
          <Link
            to="/workspace"
            className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground transition flex items-center gap-1.5"
            data-testid="account-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to workspace
          </Link>
          <div className="flex items-center gap-2.5">
            <UserAvatar user={user} size="md" />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{user.name || "(no name)"}</div>
              <div className="text-[11px] font-mono text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 lg:px-8 py-10">
        <div className="text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-2">[ account ]</div>
        <h1 className="font-display font-extrabold tracking-tight text-3xl lg:text-4xl mb-2">Your account</h1>
        <p className="text-muted-foreground mb-8">
          Update how Nexa addresses you, change your sign-in email, or rotate your password.
        </p>

        <div className="space-y-6">
          <NameCard user={user} onSaved={setUser} />
          <EmailCard user={user} onSaved={setUser} />
          <PasswordCard />
          <OnboardingDataCard user={user} onSaved={setUser} />
          <LanguageCard />
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
function LanguageCard() {
  const { t } = useTranslation();
  return (
    <Card icon={Globe} title={t("account.lang.title", "Application language")} subtitle={t("account.lang.subtitle", "Choose your language.")} testid="lang-card">
      <LanguageSwitcher variant="tabs" />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AI personalization — lets the user edit the onboarding data after first
// login. Saving here re-PUTs `/auth/profile`, refreshing context_profile
// on every subsequent LLM call. Independent of the UI locale (LanguageCard).
// ---------------------------------------------------------------------------
const ROLE_KEYS = [
  "engineer", "pm", "data", "designer", "founder", "marketing",
  "researcher", "student", "lawyer", "doctor", "finance", "ops", "other",
];
const TONE_KEYS = ["professional", "friendly", "technical", "socratic"];

function OnboardingDataCard({ user, onSaved }) {
  const { t } = useTranslation();
  const profile = user?.context_profile || {};
  const [roleKey, setRoleKey] = useState(profile.role_key || "");
  const [background, setBackground] = useState(profile.background || "");
  const [tone, setTone] = useState(profile.tone || "professional");
  const [language, setLanguage] = useState(toIsoCode(profile.language) || "en");
  const [busy, setBusy] = useState(false);

  const dirty =
    roleKey !== (profile.role_key || "") ||
    background !== (profile.background || "") ||
    tone !== (profile.tone || "professional") ||
    language !== toIsoCode(profile.language);

  const canSave = !!roleKey && !!background.trim() && dirty && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const roleLabel = t(`account:onboarding.roles.${roleKey}`);
      const { data } = await api.put("/auth/profile", {
        role_key: roleKey,
        role_label: roleLabel,
        background: background.trim(),
        tone,
        language,
      });
      onSaved?.(data);
      toast.success(t("account:profile.saved", "Onboarding data saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("account:profile.save_failed", "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      icon={Sparkles}
      title={t("account:profile.title", "Onboarding data")}
      subtitle={t("account:profile.subtitle", "Update how the AI addresses you and which language it answers in.")}
      testid="onboarding-data-card"
    >
      <div className="space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.role", "Your role")}
          </Label>
          <Select value={roleKey} onValueChange={setRoleKey}>
            <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="account-role-select">
              <SelectValue placeholder={t("account:onboarding.role_placeholder", "Pick the closest match…")} />
            </SelectTrigger>
            <SelectContent>
              {ROLE_KEYS.map((k) => (
                <SelectItem key={k} value={k} data-testid={`account-role-option-${k}`}>
                  {t(`account:onboarding.roles.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider font-mono">
            {t("account:onboarding.background", "Your background")}
          </Label>
          <Textarea
            value={background}
            onChange={(e) => setBackground(e.target.value)}
            rows={4}
            maxLength={500}
            placeholder={t("account:onboarding.background_placeholder", "")}
            className="mt-1.5 rounded-sm"
            data-testid="account-background-textarea"
          />
          <div className="text-[11px] font-mono text-muted-foreground mt-1">
            {background.length}/500
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.tone", "Preferred tone")}
            </Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="account-tone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TONE_KEYS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`account-tone-option-${k}`}>
                    {t(`account:onboarding.tones.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider font-mono">
              {t("account:onboarding.language", "AI response language")}
            </Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="mt-1.5 h-11 rounded-sm" data-testid="account-language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} data-testid={`account-language-option-${opt.value}`}>
                    {t(opt.labelKey, opt.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] font-mono text-muted-foreground mt-1">
              {t("account:profile.lang_hint", "This controls the AI's response language. Separate from the app UI language above.")}
            </p>
          </div>
        </div>

        <Button
          onClick={save}
          disabled={!canSave}
          className="rounded-sm"
          data-testid="account-profile-save"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {busy ? t("account:profile.saving", "Saving…") : t("account:profile.save", "Save changes")}
        </Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function Card({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <section className="border border-border bg-card rounded-sm" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono text-muted-foreground mb-1">
          <Icon className="h-3.5 w-3.5" />
          [ {title.toLowerCase()} ]
        </div>
        <h2 className="font-display font-bold tracking-tight text-lg">{title}</h2>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function NameCard({ user, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [busy, setBusy] = useState(false);
  const dirty = name.trim() !== (user.name || "").trim();
  const valid = name.trim().length >= 1 && name.trim().length <= 100;

  const save = async () => {
    if (!dirty || !valid) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/me/profile", { name: name.trim() });
      onSaved?.(data);
      toast.success("Name updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card icon={UserIcon} title="Profile" subtitle="Update how Nexa addresses you." testid="account-name-card">
      <Label className="text-xs uppercase tracking-wider font-mono">Display name</Label>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
        placeholder="e.g. Alex Chen"
        className="mt-1.5 rounded-sm"
        data-testid="account-name-input"
      />
      <Button
        onClick={save}
        disabled={!dirty || !valid || busy}
        className="rounded-sm mt-4"
        data-testid="account-name-save"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
        {busy ? "Saving…" : "Save name"}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function EmailCard({ user, onSaved }) {
  const [email, setEmail] = useState(user.email || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const dirty = email.trim().toLowerCase() !== (user.email || "").toLowerCase();
  const canSubmit = dirty && validEmail && password.length > 0 && !busy;

  const save = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.patch("/me/email", {
        new_email: email.trim(),
        current_password: password,
      });
      onSaved?.(data);
      setPassword("");
      toast.success("Email updated — sign in with the new email next time");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card icon={Mail} title="Change email" subtitle="Used for sign-in and password recovery." testid="account-email-card">
      <Label className="text-xs uppercase tracking-wider font-mono">New email</Label>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mt-1.5 rounded-sm"
        data-testid="account-email-input"
      />

      <Label className="text-xs uppercase tracking-wider font-mono mt-4 block">Confirm with current password</Label>
      <div className="relative mt-1.5">
        <Input
          type={showPw ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your current password"
          className="rounded-sm pr-9"
          autoComplete="current-password"
          data-testid="account-email-password"
        />
        <button
          type="button"
          onClick={() => setShowPw((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
          aria-label={showPw ? "Hide password" : "Show password"}
        >
          {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
        </button>
      </div>

      <Button
        onClick={save}
        disabled={!canSubmit}
        className="rounded-sm mt-4"
        data-testid="account-email-save"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
        {busy ? "Updating…" : "Update email"}
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const rules = useMemo(() => ({
    length: next.length >= 8,
    letter: /[A-Za-z]/.test(next),
    digit: /\d/.test(next),
  }), [next]);

  const allRulesPass = rules.length && rules.letter && rules.digit;
  const matches = next.length > 0 && next === confirm;
  const canSubmit = current.length > 0 && allRulesPass && matches && !busy;

  const save = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.patch("/me/password", {
        current_password: current,
        new_password: next,
      });
      setCurrent(""); setNext(""); setConfirm("");
      toast.success("Password changed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card icon={KeyRound} title="Change password" subtitle="Min 8 chars · letters + digits required." testid="account-password-card">
      <Label className="text-xs uppercase tracking-wider font-mono">Current password</Label>
      <PwInput
        value={current}
        onChange={setCurrent}
        show={showCur}
        onToggleShow={() => setShowCur((s) => !s)}
        autoComplete="current-password"
        testid="account-pw-current"
      />

      <Label className="text-xs uppercase tracking-wider font-mono mt-4 block">New password</Label>
      <PwInput
        value={next}
        onChange={setNext}
        show={showNew}
        onToggleShow={() => setShowNew((s) => !s)}
        autoComplete="new-password"
        testid="account-pw-new"
      />
      {next.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-3 text-[11px] font-mono" data-testid="account-pw-rules">
          <Rule ok={rules.length} label="8+ chars" />
          <Rule ok={rules.letter} label="letter" />
          <Rule ok={rules.digit} label="digit" />
        </ul>
      )}

      <Label className="text-xs uppercase tracking-wider font-mono mt-4 block">Confirm new password</Label>
      <PwInput
        value={confirm}
        onChange={setConfirm}
        show={showNew}
        onToggleShow={() => setShowNew((s) => !s)}
        autoComplete="new-password"
        testid="account-pw-confirm"
      />
      {confirm.length > 0 && !matches && (
        <div className="mt-1.5 text-[11px] font-mono text-destructive" data-testid="account-pw-mismatch">
          Passwords don't match.
        </div>
      )}

      <Button
        onClick={save}
        disabled={!canSubmit}
        className="rounded-sm mt-4"
        data-testid="account-pw-save"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
        {busy ? "Changing…" : "Change password"}
      </Button>
    </Card>
  );
}

function PwInput({ value, onChange, show, onToggleShow, autoComplete, testid }) {
  return (
    <div className="relative mt-1.5">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="rounded-sm pr-9"
        data-testid={testid}
      />
      <button
        type="button"
        onClick={onToggleShow}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-1 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
      {ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {label}
    </li>
  );
}
